import {ChevronDownIcon} from '@heroicons/react/solid';
import type {ChangeEvent, JSX} from 'react';
import React, {useEffect, useState} from 'react';

import {EUFlag} from '@/constants/countries';
import currencies from '@/constants/currencies';
import {useCountryList} from '@/context/CountryListContext';
import type {ICurrency} from '@/types/Currency';
import {formatWithCommas} from '@/utils/commaFormatter';
import {mapCountryCodeToFlags} from '@/utils/countries';

type TransactionFiltersProps = {
  selectedCurrency: ICurrency | null;
  onCurrencyChange: (name: string, currency: ICurrency) => void;
  onAmountChange: (name: string, value: number) => void;
  name: string;
  amount: number;
  className?: string;
};

export default function CurrencyInput({
  selectedCurrency,
  onAmountChange,
  onCurrencyChange,
  amount,
  name,
  className,
}: TransactionFiltersProps): JSX.Element {
  const {state: countryListState} = useCountryList();
  const {countryList} = countryListState;
  const [showCountries, setShowCountries] = useState<boolean>(false);
  const [inputValue, setInputValue] = useState<number>(amount);
  const [displayValue, setDisplayValue] = useState('');

  const selectCurrency = (currency: ICurrency) => {
    onCurrencyChange(name, currency);
    setShowCountries(false);
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value;
    //allow digits and at most one dot
    const numericValue = rawValue
      .replace(/[^0-9.]/g, '')
      .replace(/(\..*)\./g, '$1');
    const formattedNumericValue = parseFloat(numericValue) || 0;
    setDisplayValue(formatWithCommas(numericValue));
    setInputValue(formattedNumericValue);
    onAmountChange(name, formattedNumericValue);
  };

  useEffect(() => {
    if (amount !== inputValue) {
      setInputValue(amount);
      setDisplayValue(formatWithCommas(String(amount)));
    }
    // do not add inputValue to dependencies. just ignore the eslint warning
  }, [amount]);

  return (
    <div className={`relative ${className}`}>
      <div className="flex w-full relative">
        {/* from */}
        <span className="absolute top-0 left-0 pt-1 px-2.5 text-sm text-gray-500">
          {name}
        </span>
        <input
          onChange={handleChange}
          type="text"
          value={displayValue}
          name={name}
          autoComplete="off"
          className="h-12 pb-0 pt-4 border-gray-300 dark:border-dark-borderHeavy rounded text-gray-900 dark:text-white bg-gray-100 focus:ring-indigo-500 focus:bg-white focus:border-blue-500 block flex-1 min-w-0 w-full sm:text-sm p-2.5 dark:bg-dark-inputBg"
          placeholder="0.00"
        />
        {/* to */}
        <span className="absolute bottom-0 right-0 pr-2 hover:text-indigo-500">
          <button
            className="inline-flex items-center mb-1"
            onClick={() => setShowCountries(!showCountries)}
          >
            {!selectedCurrency && (
              <span className="text-sm text-gray-800 dark:text-gray-200 font-medium">
                Select Currency
              </span>
            )}
            <ChevronDownIcon className="h-5 w-5 hover:text-indigo-500 dark:hover:text-indigo-300" />
            {selectedCurrency && (
              <React.Fragment>
                {selectedCurrency.code}
                {
                  mapCountryCodeToFlags(countryList)[
                    selectedCurrency.countryCode
                  ]
                }
              </React.Fragment>
            )}
          </button>
        </span>
      </div>
      {/* options */}
      {showCountries && (
        <div
          onMouseLeave={() => setShowCountries(false)}
          className="absolute z-10 -top-2 right-0 rounded bg-white dark:bg-dark-inputBg py-2 border shadow-md dark:border-dark-borderMid overflow-auto max-h-60"
        >
          <ul>
            {currencies.map((currency, index) => (
              <li key={`${currency.countryCode}-${index}`}>
                <button
                  onClick={() => selectCurrency(currency)}
                  key={currency.code}
                  className="hover:bg-gray-100 dark:hover:bg-gray-700 active:bg-indigo-200 w-full px-3 py-2 text-left"
                >
                  {currency.code === 'EUR'
                    ? EUFlag
                    : mapCountryCodeToFlags(countryList)[currency.countryCode]}
                  &nbsp; {currency.code}
                  &nbsp;-&nbsp;
                  {currency.name}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
